import React, { useCallback, useEffect, useState } from 'react';
import { Loader, Center } from '@mantine/core';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { DEFAULT_THEME_COLOR, normalizeThemeColor } from './config/themeColors';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AppLayout from './layout/AppLayout';
import ClientPortalLayout from './layout/ClientPortalLayout';
import JlfcThemeProvider from './context/JlfcThemeProvider';
import { ActiveExploitantProvider } from './context/ActiveExploitantContext';
import { authFetch, clearAuthSession, getAuthToken, parseJsonResponse } from './lib/api';
import { isMobilePhone } from './lib/device';
import { isClientPortalUser } from './lib/moduleAccess';

function resolveThemeColor(user) {
  return normalizeThemeColor(user?.theme_color || DEFAULT_THEME_COLOR);
}

export default function App() {
  const mobileBlocked = isMobilePhone();
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  const logout = useCallback(() => {
    clearAuthSession();
    setUser(null);
  }, []);

  const handleUserUpdate = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  useEffect(() => {
    if (mobileBlocked) {
      clearAuthSession();
      setUser(null);
      setBooting(false);
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setBooting(false);
      return;
    }
    authFetch('/api/me')
      .then((r) => parseJsonResponse(r))
      .then((d) => {
        if (d.success) setUser(d.user);
        else clearAuthSession();
      })
      .catch(() => clearAuthSession())
      .finally(() => setBooting(false));
  }, [mobileBlocked]);

  const themeColor = resolveThemeColor(user);

  if (booting) {
    return (
      <JlfcThemeProvider themeColor={DEFAULT_THEME_COLOR}>
        <Center h="100vh">
          <Loader type="dots" />
        </Center>
      </JlfcThemeProvider>
    );
  }

  return (
    <JlfcThemeProvider themeColor={themeColor}>
      <BrowserRouter>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="*"
            element={
              user && !mobileBlocked ? (
                isClientPortalUser(user) ? (
                  <ClientPortalLayout user={user} onLogout={logout} />
                ) : (
                  <ActiveExploitantProvider user={user}>
                    <AppLayout user={user} onLogout={logout} onUserUpdate={handleUserUpdate} />
                  </ActiveExploitantProvider>
                )
              ) : (
                <LoginPage onLogin={setUser} />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </JlfcThemeProvider>
  );
}
